import { useEffect, useState } from "react";
import { PawPrint, Check, X } from "lucide-react";
import { api, mensajeError } from "../api/client";
import { PageHeader } from "../components/ui/PageHeader";
import { Spinner } from "../components/ui/Spinner";
import { EmptyState } from "../components/ui/EmptyState";
import { Badge } from "../components/ui/Badge";

interface SolicitudTransferencia {
  id: string;
  estado: "PENDIENTE" | "ACEPTADA" | "RECHAZADA" | "CANCELADA";
  createdAt: string;
  mascota: { id: string; nombre: string; especie: string; raza?: string | null; fotoUrl?: string | null };
  tenantOrigen: { nombre: string; subdominio: string };
}

function estadoColor(estado: string): "verde" | "amarillo" | "rojo" | "gris" {
  if (estado === "ACEPTADA") return "verde";
  if (estado === "PENDIENTE") return "amarillo";
  if (estado === "RECHAZADA") return "rojo";
  return "gris";
}

export default function Transferencias() {
  const [items, setItems] = useState<SolicitudTransferencia[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [procesando, setProcesando] = useState<string | null>(null);

  function cargar() {
    setCargando(true);
    api
      .get("/transferencias")
      .then((res) => setItems(res.data.items ?? []))
      .catch((err) => setError(mensajeError(err)))
      .finally(() => setCargando(false));
  }

  useEffect(() => {
    cargar();
  }, []);

  async function aceptar(id: string) {
    if (!confirm("¿Aceptar esta transferencia? Se creará un expediente nuevo con el perfil básico de la mascota en tu negocio.")) return;
    setProcesando(id);
    try {
      await api.post(`/transferencias/${id}/aceptar`);
      cargar();
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setProcesando(null);
    }
  }

  async function rechazar(id: string) {
    if (!confirm("¿Rechazar esta solicitud de transferencia?")) return;
    setProcesando(id);
    try {
      await api.post(`/transferencias/${id}/rechazar`);
      cargar();
    } catch (err) {
      alert(mensajeError(err));
    } finally {
      setProcesando(null);
    }
  }

  return (
    <div>
      <PageHeader
        titulo="Transferencias de expediente"
        descripcion="Solicitudes de clientes de otros negocios de la red de Mascotas Vet que quieren replicar el expediente de su mascota aquí."
        icono={<PawPrint size={20} />}
      />

      {cargando ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : items.length === 0 ? (
        <EmptyState titulo="No hay solicitudes de transferencia" descripcion="Aquí aparecerán cuando un cliente de otro negocio pida traer a su mascota contigo." />
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} className="card flex flex-wrap items-center gap-4 p-4">
              <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl bg-brand-50 text-brand-500">
                {s.mascota.fotoUrl ? (
                  <img src={s.mascota.fotoUrl} alt={s.mascota.nombre} className="h-full w-full object-cover" />
                ) : (
                  <PawPrint size={20} />
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-slate-800">{s.mascota.nombre}</p>
                <p className="text-xs text-slate-400">
                  {s.mascota.especie} · {s.mascota.raza || "Sin raza"} · Viene de {s.tenantOrigen.nombre}
                </p>
                <p className="text-xs text-slate-300">{new Date(s.createdAt).toLocaleDateString("es-MX")}</p>
              </div>
              {s.estado === "PENDIENTE" ? (
                <div className="flex gap-2">
                  <button className="btn-secondary !py-1.5 !text-xs" disabled={procesando === s.id} onClick={() => rechazar(s.id)}>
                    <X size={14} /> Rechazar
                  </button>
                  <button className="btn-primary !py-1.5 !text-xs" disabled={procesando === s.id} onClick={() => aceptar(s.id)}>
                    <Check size={14} /> Aceptar
                  </button>
                </div>
              ) : (
                <Badge color={estadoColor(s.estado)}>{s.estado}</Badge>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
